import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { assetsAPI } from '../../services/api';

// "How is my asset doing and what should I fix?" — health score, the checks
// behind it, up to five to-dos and this week vs last week. Same data as the
// weekly digest email.

type Status = 'good' | 'warn' | 'bad';

interface Health {
  score: number | null;
  grade: string | null;
  checks: { key: string; label: string; status: Status; detail: string }[];
  todos: { key: string; title: string; why: string; path: string; severity: Status }[];
  changes?: {
    page_views: { this_week: number; last_week: number };
    visitors: { this_week: number; last_week: number };
    uptime_pct: { this_week: number | null; last_week: number | null };
    avg_response_ms: { this_week: number | null; last_week: number | null };
    incidents: number;
    downtime_minutes: number;
  };
}

const COLORS: Record<Status, string> = { good: '#10b981', warn: '#f59e0b', bad: '#ef4444' };
const ICONS: Record<Status, string> = { good: 'bi-check-circle-fill', warn: 'bi-exclamation-circle-fill', bad: 'bi-x-circle-fill' };

const scoreColor = (s: number) => (s >= 75 ? COLORS.good : s >= 50 ? COLORS.warn : COLORS.bad);

const card: React.CSSProperties = {
  background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0',
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)', padding: 16,
};

const Trend: React.FC<{ label: string; now: number | null; before: number | null; format: (n: number) => string; higherIsBetter?: boolean }> =
  ({ label, now, before, format, higherIsBetter = true }) => {
    let delta: React.ReactNode = null;
    if (now !== null && before !== null && before !== 0) {
      const change = ((now - before) / before) * 100;
      if (Math.abs(change) >= 1) {
        const good = higherIsBetter ? change > 0 : change < 0;
        delta = (
          <span style={{ color: good ? COLORS.good : COLORS.bad, fontSize: '0.7rem', fontWeight: 600, marginLeft: 6 }}>
            <i className={`bi ${change > 0 ? 'bi-arrow-up' : 'bi-arrow-down'}`}></i>{Math.abs(change).toFixed(0)}%
          </span>
        );
      }
    }
    return (
      <div style={{ minWidth: 0 }}>
        <div style={{ color: '#94a3b8', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600 }}>{label}</div>
        <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '1.05rem' }}>
          {now === null ? '—' : format(now)}{delta}
        </div>
      </div>
    );
  };

const AssetHealthPanel: React.FC<{ assetId: number }> = ({ assetId }) => {
  const [health, setHealth] = useState<Health | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setHealth(null);
    setFailed(false);
    assetsAPI.getHealth(assetId)
      .then((r) => { if (!cancelled) setHealth(r.data?.data || null); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [assetId]);

  if (failed) return null;
  if (!health) {
    return <div style={{ ...card, marginBottom: 12, color: '#94a3b8', fontSize: '0.8rem' }}>Checking your asset's health…</div>;
  }

  const c = health.changes;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.4fr)', gap: 12, marginBottom: 12 }} className="asset-health-panel">
      <style>{`@media (max-width: 900px) { .asset-health-panel { grid-template-columns: 1fr !important; } }`}</style>

      {/* Score + checks */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
          {health.score !== null ? (
            <div style={{
              width: 64, height: 64, borderRadius: '50%', flexShrink: 0,
              background: `conic-gradient(${scoreColor(health.score)} ${health.score * 3.6}deg, #e2e8f0 0deg)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <div style={{ width: 50, height: 50, borderRadius: '50%', background: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontWeight: 800, fontSize: '1.05rem', color: '#0f172a', lineHeight: 1 }}>{health.score}</span>
                <span style={{ fontSize: '0.6rem', color: '#94a3b8' }}>/100</span>
              </div>
            </div>
          ) : null}
          <div>
            <div style={{ fontWeight: 700, color: '#0f172a' }}>
              Health{health.grade ? ` · Grade ${health.grade}` : ''}
            </div>
            <div style={{ color: '#64748b', fontSize: '0.78rem' }}>
              {health.score === null ? 'Not enough data yet — finish setup to get a score.'
                : health.todos.length === 0 ? 'Everything looks good.'
                : `${health.todos.length} thing${health.todos.length === 1 ? '' : 's'} to fix`}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {health.checks.map((ch) => (
            <div key={ch.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8rem' }}>
              <i className={`bi ${ICONS[ch.status]}`} style={{ color: COLORS[ch.status] }}></i>
              <span style={{ color: '#334155', fontWeight: 600 }}>{ch.label}</span>
              <span style={{ color: '#64748b', marginLeft: 'auto', textAlign: 'right' }}>{ch.detail}</span>
            </div>
          ))}
        </div>
      </div>

      {/* To-dos + this week */}
      <div style={card}>
        <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>
          <i className="bi bi-list-check me-2" style={{ color: '#6366f1' }}></i>What to fix
        </div>
        {health.todos.length === 0 ? (
          <div style={{ color: '#64748b', fontSize: '0.8rem', marginBottom: 12 }}>
            <i className="bi bi-emoji-smile me-1" style={{ color: COLORS.good }}></i>Nothing to fix right now.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
            {health.todos.map((t) => (
              <Link key={t.key} to={t.path} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 10,
                border: '1px solid #e2e8f0', textDecoration: 'none', background: '#f8fafc',
              }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: COLORS[t.severity], flexShrink: 0 }}></span>
                <span style={{ minWidth: 0, flex: 1 }}>
                  <span style={{ display: 'block', color: '#0f172a', fontWeight: 600, fontSize: '0.8rem' }}>{t.title}</span>
                  <span style={{ display: 'block', color: '#64748b', fontSize: '0.72rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.why}</span>
                </span>
                <i className="bi bi-chevron-right" style={{ color: '#94a3b8' }}></i>
              </Link>
            ))}
          </div>
        )}
        {c && (
          <>
            <div style={{ color: '#94a3b8', fontSize: '0.7rem', marginBottom: 6 }}>This week vs last week</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 10 }}>
              <Trend label="Visitors" now={c.visitors.this_week} before={c.visitors.last_week} format={(n) => n.toLocaleString()} />
              <Trend label="Page views" now={c.page_views.this_week} before={c.page_views.last_week} format={(n) => n.toLocaleString()} />
              <Trend label="Uptime" now={c.uptime_pct.this_week} before={c.uptime_pct.last_week} format={(n) => `${n.toFixed(2)}%`} />
              <Trend label="Response" now={c.avg_response_ms.this_week} before={c.avg_response_ms.last_week} format={(n) => `${n} ms`} higherIsBetter={false} />
            </div>
            {c.incidents > 0 && (
              <div style={{ color: COLORS.bad, fontSize: '0.75rem', marginTop: 8 }}>
                <i className="bi bi-exclamation-triangle me-1"></i>
                {c.incidents} outage{c.incidents === 1 ? '' : 's'} this week ({c.downtime_minutes} min)
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AssetHealthPanel;
